import React, { useState, useEffect } from 'react';

/**
 * MatchTimeControls Component
 * Handles match clock display and all time-related controls
 * 
 * @param {Object} clock - Clock state { minute, second, running, half, injuryTime }
 * @param {Function} onStartClock - Start clock callback
 * @param {Function} onStopClock - Stop clock callback
 * @param {Function} onResetClock - Reset clock callback
 * @param {Function} onSetTime - Set manual time callback (minute, second)
 * @param {Function} onSetInjuryTime - Set injury time callback (minutes)
 * @param {Function} onChangeHalf - Change half callback (halfNumber)
 * @param {boolean} joined - Whether WebSocket is connected
 */
export const MatchTimeControls = ({
  clock,
  onStartClock,
  onStopClock,
  onResetClock,
  onSetTime,
  onSetInjuryTime,
  onChangeHalf,
  joined = false
}) => {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [manualTime, setManualTime] = useState({ minute: 0, second: 0 });
  const [injuryMinutes, setInjuryMinutes] = useState(0);

  // Sync with clock state ONLY when panel opens (not on every clock tick)
  useEffect(() => {
    if (isAdvancedOpen) {
      setManualTime({ minute: clock.minute, second: clock.second });
      setInjuryMinutes(clock.injuryTime);
    }
  }, [isAdvancedOpen]); // Only sync when panel opens, not on clock changes

  const handleManualTimeSet = () => {
    onSetTime(manualTime.minute, manualTime.second);
    // Keep panel open for multiple adjustments (consistent with injury time behavior)
  };

  const handleInjuryTimeSet = () => {
    onSetInjuryTime(injuryMinutes);
  };

  const getHalfLabel = (halfNumber) => {
    switch(halfNumber) {
      case 1: return '1st';
      case 2: return '2nd';
      case 3: return 'ET1';
      case 4: return 'ET2';
      case 5: return 'PEN';
      default: return `${halfNumber}`;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg mb-4">
      {/* Main Clock Section */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-t-lg">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold">Match Time</h2>
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
            clock.running ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-800'
          }`}>
            {clock.running ? '▶ Running' : '⏸ Stopped'}
          </span>
        </div>

        {/* Clock + Controls row */}
        <div className="flex items-center justify-between gap-4">
          {/* Clock Display */}
          <div className="text-4xl font-mono font-bold text-orange-600">
            {String(clock.minute).padStart(2, '0')}:{String(clock.second).padStart(2, '0')}
            {clock.injuryTime > 0 && clock.minute >= 45 && (
              <span className="text-xl text-red-600 ml-1">+{clock.injuryTime}</span>
            )}
          </div>

          {/* Half Selector */}
          <div className="flex gap-1">
            {[1, 2, 3, 4].map(halfNum => (
              <button
                key={halfNum}
                onClick={() => onChangeHalf(halfNum)}
                disabled={!joined}
                className={`px-3 py-1.5 rounded text-xs font-semibold ${
                  clock.half === halfNum
                    ? 'bg-orange-600 text-white'
                    : 'bg-white border border-gray-300 hover:bg-gray-50 disabled:bg-gray-100'
                }`}
              >
                {getHalfLabel(halfNum)}
              </button>
            ))}
          </div>

          {/* Quick Controls */}
          <div className="flex gap-2">
            <button
              onClick={onStartClock}
              disabled={!joined || clock.running}
              className="px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 font-bold text-sm"
            >
              Start
            </button>
            <button
              onClick={onStopClock}
              disabled={!joined || !clock.running}
              className="px-4 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 font-bold text-sm"
            >
              Stop
            </button>
            <button
              onClick={() => onResetClock(clock.half)}
              disabled={!joined}
              className="px-4 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-300 font-bold text-sm"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Clock Controls (Collapsible) */}
      <details 
        open={isAdvancedOpen}
        onToggle={(e) => setIsAdvancedOpen(e.target.open)}
        className="border-t"
      >
        <summary className="p-4 font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer flex items-center gap-2">
          <span>⏱️</span>
          <span>Advanced Clock Controls</span>
          <span className="ml-auto text-gray-400">▼</span>
        </summary>
        <div className="p-4 border-t space-y-4">
          {/* Manual Time Adjustment */}
          <div>
            <label className="block text-sm font-semibold mb-2">Manual Time Adjustment:</label>
            <div className="flex gap-2">
              <input 
                type="number" 
                placeholder="Min" 
                className="w-20 px-3 py-2 border rounded" 
                value={manualTime.minute}
                onChange={(e) => setManualTime({ ...manualTime, minute: parseInt(e.target.value) || 0 })}
                min="0"
                max="120"
              />
              <span className="text-xl self-center">:</span>
              <input 
                type="number" 
                placeholder="Sec" 
                className="w-20 px-3 py-2 border rounded" 
                value={manualTime.second}
                onChange={(e) => setManualTime({ ...manualTime, second: parseInt(e.target.value) || 0 })}
                min="0"
                max="59"
              />
              <button 
                onClick={handleManualTimeSet}
                disabled={!joined}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
              >
                Set Time
              </button>
            </div>
          </div>

          {/* Injury Time */}
          <div>
            <label className="block text-sm font-semibold mb-2">Injury Time:</label>
            <div className="flex gap-2 items-center">
              <input 
                type="number" 
                placeholder="0" 
                className="w-24 px-3 py-2 border rounded" 
                value={injuryMinutes}
                onChange={(e) => setInjuryMinutes(parseInt(e.target.value) || 0)}
                min="0"
                max="15"
              />
              <button 
                onClick={handleInjuryTimeSet}
                disabled={!joined}
                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-300"
              >
                Set +Time
              </button>
              <span className="text-sm text-gray-600">Current: +{clock.injuryTime} min</span>
            </div>
          </div>

          {/* Reset Clock */}
          <div>
            <button 
              onClick={() => onResetClock(clock.half)}
              disabled={!joined}
              className="w-full px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:bg-gray-300 font-semibold"
            >
              🔄 Reset Clock to 00:00
            </button>
          </div>
        </div>
      </details>
    </div>
  );
};
